import {Center, getSize, HStack, Icon, LinkProps, Show, Text} from 'platform/foundation';
import {css, useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId} from 'shared/web';

type ButtonLinkVariant = 'primary' | 'inverted';

interface ButtonLinkProps extends LinkProps {
  /** `primary` is the accent button; `inverted` is a white pill for cards on a tinted background. */
  variant?: ButtonLinkVariant;
}

export function ButtonLink(props: ButtonLinkProps) {
  const theme = useTheme();
  const isInverted = props.variant === 'inverted';
  const padding = match(props.size)
    .with('base', () => `${getSize(3)} ${getSize(6)}`)
    .otherwise(() => `${getSize(2)} ${getSize(4)}`);
  // Resolved here as plain values: the `css` prop is hoisted to module scope, so a function
  // interpolation cannot read component variables such as `isInverted`.
  const color = isInverted ? theme.colors.text.primary : theme.colors.text.white;
  const borderRadius = isInverted ? theme.radii.border_radius_full : theme.radii.small;
  const backgroundColor = isInverted ? theme.colors.fill.white : theme.colors.accent.base;
  const hoverBackgroundColor = isInverted ? theme.colors.fill.white : theme.colors.accent.dark;
  const hoverBoxShadow = isInverted
    ? `0 0 0 ${INVERTED_HOVER_RING_WIDTH} ${theme.colors.border.emphasisDefault}`
    : 'none';
  // Inverted icons keep their own colours (e.g. the PDF file type icon).
  const iconColor = isInverted ? undefined : 'general.white';

  return (
    <a
      rel={props.rel}
      target={props.target}
      download={props.download}
      href={props.href ?? '#'}
      onClick={props.isDisabled ? (e) => e.preventDefault() : props.onClick}
      aria-disabled={props.isDisabled}
      css={css`
        display: inline-block;
        color: ${color};
        cursor: ${props.isDisabled ? 'auto' : 'pointer'};
        opacity: ${props.isDisabled ? 0.5 : undefined};
        pointer-events: ${props.isDisabled ? 'none' : 'all'};
        padding: ${padding};
        border-radius: ${borderRadius};
        background-color: ${backgroundColor};
        &:hover {
          text-decoration: none;
          background-color: ${hoverBackgroundColor};
          box-shadow: ${hoverBoxShadow};
        }
      `}
      data-testid={suffixTestId('buttonLink', props)}
    >
      <HStack spacing={2} align="center">
        <Show when={isNotNil(props.leftIcon)}>
          <Icon size={5} color={iconColor} value={props.leftIcon} />
        </Show>
        <Center minHeight={5}>
          <Text
            color={isInverted ? 'primary' : 'white'}
            size="small"
            data-testid={suffixTestId('buttonLinkContent', props)}
          >
            {props.title}
          </Text>
        </Center>
        <Show when={isNotNil(props.rightIcon)}>
          <Icon size={5} color={iconColor} value={props.rightIcon} />
        </Show>
      </HStack>
    </a>
  );
}

// The inverted pill sits on a tinted card, so hover draws a hairline ring instead of a fill change.
const INVERTED_HOVER_RING_WIDTH = '1px';
